<script setup>
import { computed, onMounted, onUnmounted, watch } from 'vue'
import { useStore } from 'vuex'
import { MapLayer, MapSource } from '@/components/map'
import {
  LayerPlanetVars,
  LayerOrder,
  LayerRadar,
  LayerCollectedData,
  LayerStatic,
  LayerTasking,
  LayerLulcPreview,
  LayerLulcClassified,
  LayerBasemap
} from '@/components/layers'
import { useLayerManager } from '@/composables/useLayerManager'
import { createLayerId, createSourceId } from '@/components/layers/types'

const emit = defineEmits(['isLoaded', 'deleteLayer'])
const props = defineProps({
  map: Object,
  item: Object,
  position: Number,
  layerId: String,
  orgId: Number,
  isVisible: Boolean,
})

const store = useStore()
const map = computed(() => props.map)
const layerManager = useLayerManager(map)

// Calculate beforeId from position using z-layers
// Position 0 = top of list = top of map = z-0
const beforeId = computed(() => `z-${props.position}`)

// Layer IDs using unified convention
const geometrySourceId = computed(() => createSourceId(props.layerId, 'geometry-source'))
const geometryLayerId = computed(() => createLayerId(props.layerId, 'geometry'))

// Get the geometry from the layer data
const layerGeometry = computed(() => {
  return props.item?.properties?.layer?.geometry ||
         props.item?.geometry ||
         null
})

// Register/unregister group with layer manager
onMounted(() => {
  layerManager.registerGroup(props.layerId, props.position)
  if (layerGeometry.value) {
    layerManager.registerLayer(props.layerId, geometryLayerId.value)
  }
})

onUnmounted(() => {
  if (layerGeometry.value) {
    layerManager.unregisterLayer(props.layerId, geometryLayerId.value)
  }
  layerManager.unregisterGroup(props.layerId)
})

// Watch for position changes (drag-drop reordering)
// When position changes, move all layers in this group to the new z-layer
watch(() => props.position, (newPosition, oldPosition) => {
  if (newPosition !== oldPosition && newPosition !== undefined) {
    layerManager.moveGroupToPosition(props.layerId, newPosition)
  }
})
</script>

<template>
<div class="ml-2">
  <!-- Geometry outline for the layer boundary -->
  <MapSource
    v-if="layerGeometry"
    :map="map"
    type="geojson"
    :id="geometrySourceId"
    :data="{
      type: 'Feature',
      geometry: layerGeometry,
      properties: {}
    }"
  >
    <MapLayer
      :map="map"
      :id="geometryLayerId"
      :source="geometrySourceId"
      type="line"
      :before-id="beforeId"
      :group-id="layerId"
      :options="{
        paint: {
          'line-color': '#1E90FF',
          'line-width': 2,
          'line-opacity': 0.8
        },
        layout: {
          visibility: isVisible ? 'visible' : 'none'
        }
      }"
    />
  </MapSource>

  <!-- Layer variants - each handles its own MapSource/MapLayer.
       Groups (data_type=3) never render here — their children do it themselves.
       PlanetScope (type 0) is no longer a layer — it's a basemap (see
       components/maps/basemaps.ts). -->
  <LayerOrder
    v-if="item.properties.data_type !== 3 && item.properties.type === 1"
    :map="map"
    :layer="item.properties"
    :layer-id="layerId"
    :before-id="beforeId"
    :group-id="layerId"
    :is-visible="isVisible"
    :options="{
      minzoom: 0,
      maxzoom: 22,
      layout: {
        visibility: isVisible ? 'visible' : 'none',
      },
    }"
    @isLoaded="emit('isLoaded', $event)"
  />

  <LayerPlanetVars
    v-if="item.properties.data_type !== 3 && item.properties.type === 2"
    :map="map"
    :layer-id="layerId"
    :code="item.properties.code"
    :layer="item.properties.layer"
    :before-id="beforeId"
    :group-id="layerId"
    :is-visible="isVisible"
    :options="{
      layout: {
        visibility: isVisible ? 'visible' : 'none',
      },
    }"
    @isLoaded="emit('isLoaded', $event)"
    :org-id="orgId"
  />

  <LayerRadar
    v-if="item.properties.data_type !== 3 && item.properties.type === 3"
    :map="map"
    :layer-id="layerId"
    :code="item.uuid"
    :layer="item.properties.layer"
    :before-id="beforeId"
    :group-id="layerId"
    :is-visible="isVisible"
    :options="{
      layout: {
        visibility: isVisible ? 'visible' : 'none',
      },
    }"
    @isLoaded="emit('isLoaded', $event)"
    :org-id="orgId"
  />

  <LayerCollectedData
    v-if="item.properties.data_type !== 3 && item.properties.type === 4"
    :map="map"
    :layer-id="layerId"
    :code="item.uuid"
    :layer="item.properties.layer"
    :org-id="orgId"
    :before-id="beforeId"
    :group-id="layerId"
    :is-visible="isVisible"
    :options="{
      layout: {
        visibility: isVisible ? 'visible' : 'none',
      },
    }"
    @isLoaded="emit('isLoaded', $event)"
  />

  <LayerStatic
    v-if="item.properties.data_type !== 3 && item.properties.type === 5"
    :map="map"
    :layer-id="layerId"
    :code="item.uuid"
    :layer="item.properties.layer"
    :org-id="orgId"
    :before-id="beforeId"
    :group-id="layerId"
    :is-visible="isVisible"
    :options="{
      layout: {
        visibility: isVisible ? 'visible' : 'none',
      },
    }"
    @isLoaded="emit('isLoaded', $event)"
  />

  <!-- Tasking layers get promoted to a group (data_type 3) once a Hi-Res order
       is created from a capture (see apiv3 create_order_from_capture). Unlike the
       other variants, LayerTasking must still render for the group itself so the
       capture footprints/imagery show — its child order sublayers render in
       addition, via MapLayers' group-children loop. -->
  <LayerTasking
    v-if="item.properties.type === 6"
    :map="map"
    :layer="item"
    :layer-id="layerId"
    :before-id="beforeId"
    :group-id="layerId"
    :is-visible="isVisible"
    :options="{
      layout: {
        visibility: isVisible ? 'visible' : 'none',
      },
    }"
    @isLoaded="emit('isLoaded', $event)"
  />

  <LayerBasemap
    v-if="item.properties.data_type !== 3 && item.properties.type === 8"
    :map="map"
    :layer="item.properties.layer"
    :layer-id="layerId"
    :before-id="beforeId"
    :group-id="layerId"
    :is-visible="isVisible"
    @isLoaded="emit('isLoaded', $event)"
  />

  <!-- LULC layers render the classified tile source as soon as at least one
       month finishes. The detail-page chart drives which month is active via
       the vuex lulcActiveMonth slice. While nothing is done yet, only the AOI
       border is shown via LayerLulcPreview. -->
  <LayerLulcClassified
    v-if="item.properties.data_type !== 3 && item.properties.type === 7 && !(item.properties.children?.length > 0) && (item.properties.layer?.months || []).some(m => Number(m.status) === 0)"
    :map="map"
    :layer-id="layerId"
    :before-id="beforeId"
    :group-id="layerId"
    :parent-code="item.uuid"
    :months="item.properties.layer?.months || []"
    :is-visible="isVisible"
    :aoi="item.properties.layer?.geometry || item.geometry"
    @isLoaded="emit('isLoaded', $event)"
  />
  <LayerLulcPreview
    v-else-if="item.properties.data_type !== 3 && item.properties.type === 7 && !(item.properties.children?.length > 0)"
    :map="map"
    :layer-id="layerId"
    :before-id="beforeId"
    :group-id="layerId"
    :org-id="orgId"
    :layer-uuid="item.uuid"
    :is-visible="isVisible"
    :aoi="item.properties.layer?.geometry || item.properties.layer?.aoi"
    @isLoaded="emit('isLoaded', $event)"
  />
</div>
</template>
